import type {MessageDescriptor, PrimitiveType} from 'react-intl';
import {messages} from '@smartmixin/i18n';
import BaseMoment from 'moment';
import {DateRange, extendMoment} from 'moment-range';
import moment, {Moment} from 'moment-timezone';
import * as Time from '../Time';
import {clockFormat, isMeridiemFirst, twelveHourFormat} from './clock';
import {
  dateTimeFormat,
  formatDateRange,
  formatDateRangeWithTime,
  type RangeFormatOptions,
} from './dateRange';

// Moment locales self-register via side-effect imports. Static imports work in
// both Metro (mobile) and Vite (web); dynamic import() broke Metro's lazy
// resolution from workspace packages, and require() is undefined in browser
// ESM bundles (SMARTMIXIN-WEB-34).
import './locale/ar.js';
import './locale/bg.js';
import './locale/ca.js';
import './locale/cs.js';
import './locale/da.js';
import './locale/de.js';
import './locale/el.js';
// Patches the built-in `en`; must precede en-dmy, which copies it (FE #461).
import './locale/en.js';
import './locale/en-dmy.js';
import './locale/es.js';
import './locale/fr.js';
import './locale/hr.js';
import './locale/hu.js';
import './locale/it.js';
import './locale/ja.js';
import './locale/ko.js';
import './locale/nb.js';
import './locale/nl.js';
import './locale/pl.js';
import './locale/pt.js';
import './locale/ro.js';
import './locale/ru.js';
import './locale/sk.js';
import './locale/sr-cyrl.js';
import './locale/sv.js';
import './locale/tr.js';
import './locale/uk.js';

// moment.defineLocale() switches the active locale as a side effect; restore
// English so app boot stays consistent until LanguageProvider calls setLocale.
// Guarded for tests that mock moment-timezone with a partial surface.
if (typeof moment.locale === 'function') {
  moment.locale('en');
}

// @ts-expect-error -- moment-range's extendMoment wants its own bundled moment
// type, not the moment-timezone instance this module uses everywhere else.
const momentRange = extendMoment(BaseMoment);

export {
  clockFormat,
  dateTimeFormat,
  formatDateRange,
  formatDateRangeWithTime,
  twelveHourFormat,
};
export type {
  RangeFormatOptions,
  RangeStyle,
  RangeWithTimeOptions,
} from './dateRange';

export type DateTimeConfig = {
  is_24_hour: boolean;
  month_format: string;
};

/**
 * The time on the user's clock, the 12-hour form zero-padded ("08:49 PM").
 * Japanese and Korean put the meridiem first, unpadded as they write it
 * ("午後 8:49", `twelveHourFormat`; FE #436). A new label wants
 * `clockFormat` ("8:49 PM"), and `dateTimeFormat` for a date with its time.
 */
export const hourFormatter = ({is_24_hour}: DateTimeConfig) => {
  if (is_24_hour) {
    return 'HH:mm';
  }
  return isMeridiemFirst() ? twelveHourFormat() : 'hh:mm A';
};

/**
 * A time in as few characters as the clock allows, for labels that must fit
 * a narrow column like the hourly forecast grids (FE #429): `9 AM` rather
 * than `09:00 AM`. The minutes are dropped only when they are 0 in
 * `timezone`: a whole UTC hour is 1:30 PM in India and 1:45 PM in Nepal.
 */
export const formatCompactHour = (
  timestamp: number,
  timezone: string,
  {is_24_hour}: DateTimeConfig,
) => {
  const local = timezoneDateFromTimestamp(timestamp, timezone);
  if (is_24_hour) {
    return local.format('HH:mm');
  }
  return local.format(twelveHourFormat({minutes: local.minute() !== 0}));
};

export const DAY = 'day';
export const WEEK = 'week';
export const TEN_DAYS = 'tenDays';
export const MONTH = 'month';
export const YEAR = 'year';
export const LAST_24_HOURS = 'last24Hours';
export const LAST_7_DAYS = 'last7Days';
export const LAST_30_DAYS = 'last30Days';

export const LAST_SCALE_CHOICES = [
  LAST_24_HOURS,
  LAST_7_DAYS,
  LAST_30_DAYS,
] as Time.Scale[];

export const PREVIOUS_DAY = 'previousDay';
export const SAME_DAY_LAST_YEAR = 'sameDayLastYear';
export const PREVIOUS_WEEK = 'previousWeek';
export const PREVIOUS_MONTH = 'previousMonth';
export const SAME_MONTH_LAST_YEAR = 'sameMonthLastYear';
export const PREVIOUS_YEAR = 'previousYear';
export const PREVIOUS_24_HOURS = 'previous24Hours';
export const PREVIOUS_7_DAYS = 'previous7Days';
export const PREVIOUS_30_DAYS = 'previous30Days';

/** Whether a `DateTimeConfig.month_format` puts the day first. */
export const isDayFirst = (monthFormat: string) => /^d/i.test(monthFormat);

// The last date order given to setLocale: a caller that only switches the
// language (HelpUrlLocaleProvider) keeps the user's order.
let dayFirst = false;

/**
 * Switches moment to the app language. Pass the user's date order
 * (`DateTimeConfig.month_format`) whenever it is known: moment's localized
 * long dates (`LL`, `ll`, `LLLL`…) follow the moment locale, which for English
 * is month-first, so English with a day-first order gets `en-dmy` (FE #430).
 * Other languages are day-first or have their own order already.
 */
export const setLocale = (locale: string, monthFormat?: string) => {
  if (monthFormat !== undefined) {
    dayFirst = isDayFirst(monthFormat);
  }
  moment.locale(momentLocaleFor(locale));
};

/**
 * moment's locale for an app language, in the date order last given to
 * `setLocale`: `en-dmy` for English with a day-first order, `sr-cyrl` for
 * Serbian, otherwise the language itself. For code that formats in a locale
 * of its own rather than the active one (the warnings' `time.ts`, #432), so
 * its long dates follow the user's order as the rest do.
 */
export const momentLocaleFor = (locale: string): string => {
  if (locale === 'sr') {
    return 'sr-cyrl';
  }
  if (locale === 'en' && dayFirst) {
    return 'en-dmy';
  }
  return locale;
};

/**
 * The locale to hand `Intl.DateTimeFormat` for dates in the user's order:
 * `en-GB` for English with the day-first order given to `setLocale`, which
 * Intl's `en` would write month-first (FE #430). Other languages unchanged.
 */
export const intlDateLocale = <T extends string | undefined>(locale: T) =>
  (locale === 'en' && dayFirst ? 'en-GB' : locale) as T;

// With `ss` at 0 and `s` at 60, every difference of 1–59 s is formatted with
// moment's `ss` key (0 s alone gets `s`, "a few seconds"). So every locale's
// `relativeTime.ss` must handle 1 itself: a flat "%d seconds" reads
// "1 seconds ago" (FE #461). Vendored locales are in `./locale/`; English is
// patched in `./locale/en.js`. Still wrong for 1 (upstream code, not yet
// fixed): cs, pl and sk use the plural form, hr and sr-cyrl the wrong case.
export const setTimeDifferenceThresholds = () => {
  // Change threshold for dates (see https://stackoverflow.com/questions/17473300/moment-js-change-the-fromnow-time-range)
  moment.relativeTimeThreshold('s', 60);
  moment.relativeTimeThreshold('ss', 0);
  moment.relativeTimeThreshold('m', 60);
  moment.relativeTimeThreshold('h', 60);
  moment.relativeTimeThreshold('d', 24);
  moment.relativeTimeThreshold('M', 12);
};

export const isoToTimestamp = (iso: string) => {
  const date = new Date(iso);
  return moment(date).unix();
};

export const isTimestampToday = (timestamp: number, timezone: string) =>
  dayStart(timestamp, timezone) === dayStart(now(), timezone);

export const timestampToDateString = (timestamp: number) => {
  const m = moment.unix(timestamp);
  return m.format('LL');
};

export const timestampToMoment = (timestamp: number, timezone: string) =>
  moment.tz(timestamp * 1000, timezone);

export const momentToTimestamp = (m: Moment) => m.unix();

export const timestampToCalendarString = (timestamp: number) => {
  const m = moment.unix(timestamp);
  return m.calendar();
};

export const dateStringToLocal = (dateString: string, monthFormat: string) => {
  const m = moment(dateString, 'YYYY-MM-DD');
  return [m.format('ddd'), m.format(monthFormat)].join('\n');
};

export const monthStringToLocal = (monthString: string) => {
  const m = moment(monthString, 'M');
  return m.format('MMM')[0].toUpperCase();
};

export const parseString = (datetimeString: string) => moment(datetimeString);

export const now = () => moment().unix();

export const timestampFromMonth = (
  month: number,
  year: number,
  timezone: string,
) => {
  return moment.tz({year, month: month - 1, day: 1}, timezone).unix();
};

export const timestampFromYear = (year: number, timezone: string) => {
  // 1st June of the year
  return moment.tz({year, month: 6 - 1, day: 1}, timezone).unix();
};

export const currentYear = (timezone: string) => moment().tz(timezone).year();

export const currentMonth = (timezone: string) =>
  moment().tz(timezone).month() + 1;

export const nextMonth = (year: number, month: number) => {
  const m = moment(`${year}-${month}`, 'YYYY-MM').add(1, 'months');
  return {
    year: m.year(),
    month: m.month() + 1,
  };
};

export const previousMonth = (year: number, month: number) => {
  const m = moment(`${year}-${month}`, 'YYYY-MM').subtract(1, 'months');
  return {
    year: m.year(),
    month: m.month() + 1,
  };
};

export const currentDay = (timezone: string) => moment().tz(timezone).day();

export const differenceWithNow = (
  timestamp: number,
  floor: null | 'hour' | 'day' = null,
) => {
  if (floor !== null) {
    return moment(timestamp * 1000).diff(moment().startOf(floor)) / 1000;
  }
  const diff = moment(timestamp * 1000).diff(moment()) / 1000;
  return diff;
};

export const formatTimeDifference = (timestamp: number) => {
  if (timestamp) {
    return moment(timestamp * 1000).fromNow();
  }
  return '-';
};

export const timezoneDateFromTimestamp = (
  timestamp: number,
  timezone: string,
) => moment.tz(timestamp * 1000, timezone);

export const shift = (
  timestamp: number,
  timezone: string,
  amount: number,
  unit: BaseMoment.unitOfTime.DurationConstructor,
) => {
  const m = moment.tz(timestamp * 1000, timezone);
  if (amount > 0) {
    return m.add(amount, unit).unix();
  } else {
    return m.subtract(Math.abs(amount), unit).unix();
  }
};

export const hourStart = (timestamp: number, timezone: string) => {
  const m = moment.tz(timestamp * 1000, timezone);
  return m.startOf('hour').unix();
};

export const hourEnd = (timestamp: number, timezone: string) => {
  const m = moment.tz(timestamp * 1000, timezone);
  return m.endOf('hour').unix();
};

export const dayStart = (timestamp: number, timezone: string) => {
  const m = moment.tz(timestamp * 1000, timezone);
  return m.startOf('day').unix();
};

export const dayEnd = (timestamp: number, timezone: string) => {
  const m = moment.tz(timestamp * 1000, timezone);
  return m.endOf('day').unix();
};

export const weekStart = (timestamp: number, timezone: string) => {
  const m = moment.tz(timestamp * 1000, timezone);
  return m.startOf('isoWeek').unix();
};

export const weekEnd = (timestamp: number, timezone: string) => {
  const m = moment.tz(timestamp * 1000, timezone);
  return m.endOf('isoWeek').unix();
};

export const monthStart = (timestamp: number, timezone: string) => {
  const m = moment.tz(timestamp * 1000, timezone);
  return m.startOf('month').unix();
};

export const monthEnd = (timestamp: number, timezone: string) => {
  const m = moment.tz(timestamp * 1000, timezone);
  return m.endOf('month').unix();
};

export const yearStart = (timestamp: number, timezone: string) => {
  const m = moment.tz(timestamp * 1000, timezone);
  return m.startOf('year').unix();
};

export const yearEnd = (timestamp: number, timezone: string) => {
  const m = moment.tz(timestamp * 1000, timezone);
  return m.endOf('year').unix();
};

export const extractMonth = (timestamp: number, timezone: string) => {
  const m = moment.tz(timestamp * 1000, timezone);
  return m.month();
};

export const extractYear = (timestamp: number, timezone: string) => {
  const m = moment.tz(timestamp * 1000, timezone);
  return m.year();
};

export const formatDate = (
  timestamp: number,
  timezone: string,
  formatter: string,
) => {
  if (timestamp) {
    const m = timezoneDateFromTimestamp(timestamp, timezone);
    return m.format(formatter);
  }
  return '-';
};

// Like formatDate, but numeral-stable: always ASCII digits regardless of the
// active display locale. Report data maps are keyed by these formatted date
// strings and looked up with locale-independent keys built in plain JS (see
// NetatmoCommon/Utils.extractReportData). moment's `ar` locale rewrites digits
// to Arabic-Indic (٢٠٢٦/٠٦/٠١) via postformat, so a display-formatted key never
// matched the ASCII lookup and blanked every report cell. Use this for KEYS,
// never for user-facing text (headers should stay localized).
export const formatDateKey = (
  timestamp: number,
  timezone: string,
  formatter: string,
) => {
  if (timestamp) {
    const m = timezoneDateFromTimestamp(timestamp, timezone);
    return m.locale('en').format(formatter);
  }
  return '-';
};

export const formatMonth = (year: number, month: number) => {
  const m = moment(`${year}-${month}`, 'YYYY-MM');
  return m.format('MMMM YYYY');
};

export const timeDiffUtcWithNowInMin = (timestamp: number) =>
  moment().diff(moment.unix(timestamp), 'minutes');

export const timeDiffUtcWithNowInSeconds = (timestamp: number) =>
  moment().diff(moment.unix(timestamp), 'seconds');

export const timeDiffWithMidnight = (timezone: string) => {
  const nowMoment = moment().tz(timezone);
  const beginningOfTheWeek = moment
    .tz(now() * 1000, timezone)
    .startOf('isoWeek');
  return nowMoment.diff(beginningOfTheWeek, 'minutes');
};

export const timestampAtWeekStart = (timezone: string) =>
  moment
    .tz(now() * 1000, timezone)
    .startOf('isoWeek')
    .unix();

export const currentDayOfMonth = (timezone: string) =>
  moment().tz(timezone).date();

export const daysInMonth = (year: number, month: number) =>
  moment(`${year}-${month}`, 'YYYY-MM').daysInMonth();

/**
 * The year and the punctuation that ties it to the day, in a locale's `LL`:
 * ", YYYY" (en), " [de] YYYY" (ca/es/pt), " YYYY г." (ru), "YYYY年" (ja),
 * "YYYY. " (hu), " YYYY." (sr).
 */
const LL_YEAR = /,?\s*(\[de\]\s*)?YYYY(\s*[гр]\.|[年년.])?\s*/;

/**
 * A calendar day without a year, in the active locale's own order: "October 6",
 * "6. Oktober", "6 de octubre", "10月6日", "október 6.".
 *
 * Built from the locale's long date (`LL`) with the year taken out, rather
 * than through `Intl.DateTimeFormat`, which mobile does not use. Any month and
 * day the calendar has, February 29 included (2000 is a leap year).
 */
export const formatMonthDay = (month: number, day: number) =>
  moment({year: 2000, month: month - 1, day}).format(longDayMonthFormat());

const longDayMonthFormat = () =>
  moment.localeData().longDateFormat('LL').replace(LL_YEAR, ' ').trim();

/**
 * The moment format of a day and short month without a year, in the active
 * locale's own order: "Oct 6" (en), "6 Oct" (English with the day-first date
 * order), "6. Okt." (de), "6 oct." (fr). For chart ticks (FE #428).
 */
export const dayMonthFormat = () => longDayMonthFormat().replace('MMMM', 'MMM');

/**
 * The time at the end of a locale's `LLLL`, with what ties it to the date:
 * " HH:mm" (fr), " h:mm A" (en), " A h:mm" (ko), " [kl.] HH:mm" (da/nb/sv),
 * " [a les] H:mm" (ca), ", H:mm" (ru, after "г.").
 */
const LLLL_TIME = /,?\s*(\[[^\]]*\]\s*)?(A\s+)?[Hh]{1,2}:mm(\s+A)?$/;

/**
 * The moment format of a day with its weekday, in the active locale's own
 * order: "Saturday, October 24, 2026" (en), "Saturday, 24 October 2026"
 * (English with the day-first date order), "2026. október 24., szombat" (hu),
 * "2026年10月24日 土曜日" (ja). The locale's `LLLL` with the time taken out,
 * as `dddd LL` put the weekday first in every language (FE #435).
 */
export const weekdayDateFormat = () =>
  moment.localeData().longDateFormat('LLLL').replace(LLLL_TIME, '');

export const monthListShort = () => moment.monthsShort();

export const monthList = () => moment.months();

export const getPrevious = (
  timestamp: number,
  timezone: string,
  scale: string,
) => {
  const date = timezoneDateFromTimestamp(timestamp, timezone);
  if (scale === DAY) {
    return date.subtract(1, DAY).unix();
  }
  if (scale === WEEK) {
    return date.subtract(1, 'week').unix();
  }
  if (scale === MONTH) {
    return date.subtract(1, 'month').unix();
  }
  if (scale === YEAR) {
    return date.subtract(1, 'year').unix();
  }
  return -1;
};

export const getNext = (
  timestamp: number,
  timezone: string,
  scale: Time.Scale,
) => {
  const date = timezoneDateFromTimestamp(timestamp, timezone);
  if (scale === DAY) {
    return date.add(1, 'day').unix();
  }
  if (scale === WEEK) {
    return date.add(1, 'week').unix();
  }
  if (scale === MONTH) {
    return date.add(1, 'month').unix();
  }
  if (scale === YEAR) {
    return date.add(1, 'year').unix();
  }
  return -1;
};

export const getScaleStart = (
  timestamp: number,
  timezone: string,
  scale: string,
  extended: boolean = false,
) => {
  let start: number;
  switch (scale) {
    case DAY:
      start = dayStart(
        extended ? getPrevious(timestamp, timezone, scale) : timestamp,
        timezone,
      );
      break;
    case 'last24Hours':
      start = shift(hourStart(now(), timezone), timezone, -23, 'hours');
      break;
    case 'week':
      start = weekStart(
        extended ? getPrevious(timestamp, timezone, scale) : timestamp,
        timezone,
      );
      break;
    case 'last7Days':
      start = shift(hourStart(now(), timezone), timezone, -7, 'days');
      break;
    case 'month':
      start = monthStart(
        extended ? getPrevious(timestamp, timezone, scale) : timestamp,
        timezone,
      );
      break;
    case 'last30Days':
      start = shift(dayStart(now(), timezone), timezone, -30, 'days');
      break;
    case 'year':
      start = yearStart(
        extended ? getPrevious(timestamp, timezone, scale) : timestamp,
        timezone,
      );
      break;
    default:
      start = 0;
  }
  return start;
};

/**
 * Whether a chart's Next can go past the period holding `timestamp`: the
 * following period has STARTED, in the station's timezone. Compares that
 * period's start, never the next timestamp itself -- month + 1 clamps Aug 31
 * to Sep 30, still ahead of Sep 25 while September has begun (#279).
 * The rolling scales in LAST_SCALE_CHOICES have no next period; any other
 * scale without one (getNext returns -1) must be listed there too.
 */
export const nextPeriodHasStarted = (
  timestamp: number,
  timezone: string,
  scale: Time.Scale,
) =>
  !LAST_SCALE_CHOICES.includes(scale) &&
  differenceWithNow(
    getScaleStart(getNext(timestamp, timezone, scale), timezone, scale),
  ) < 0;

export const getScaleEnd = (
  timestamp: number,
  timezone: string,
  scale: Time.Scale,
) => {
  let end: number;
  switch (scale) {
    case DAY:
      end = dayEnd(timestamp, timezone);
      break;
    case 'last24Hours':
      end = hourEnd(now(), timezone);
      break;
    case 'week':
      end = weekEnd(timestamp, timezone);
      break;
    case 'last7Days':
      end = hourStart(now(), timezone);
      break;
    case 'month':
      end = monthEnd(timestamp, timezone);
      break;
    case 'last30Days':
      end = dayEnd(now(), timezone);
      break;
    case 'year':
      end = yearEnd(timestamp, timezone);
      break;
    default:
      end = 0;
  }
  return end;
};

export const formatTimestampFromScale = (
  timestamp: number,
  timezone: string,
  scale: Time.Scale,
  options?: RangeFormatOptions,
) => {
  if (scale === DAY) {
    return formatDate(
      timestamp,
      timezone,
      options?.style === 'spoken' ? 'LL' : 'll',
    );
  }
  if ([WEEK, TEN_DAYS].includes(scale)) {
    const start = getScaleStart(timestamp, timezone, scale);
    const end = getScaleEnd(timestamp, timezone, scale);
    // TEN_DAYS has no bounds (both are 0), so there is no range to format:
    // render the same '-' placeholder formatDate uses for a missing timestamp.
    if (!start || !end) {
      return '-';
    }
    return formatDateRange(start, end, timezone, options);
  }
  if (scale === MONTH) {
    return formatDate(timestamp, timezone, 'MMMM YYYY');
  }
  if (scale === YEAR) {
    return formatDate(timestamp, timezone, 'YYYY');
  }
  return '';
};

export const getTodaySharpHours = (
  timestamp: number,
  timezone: string,
  formatter: string,
) => {
  const range = momentRange.range(
    moment.tz(getScaleStart(timestamp, timezone, 'day') * 1000, timezone),
    moment.tz(getScaleEnd(timestamp, timezone, 'day') * 1000, timezone),
  );

  const hours = Array.from(range.by('hour', {excludeEnd: false}));
  return hours.map((hour) => [
    hour.unix().toString(),
    hour.tz(timezone).format(formatter),
  ]);
};

export const groupByDay = (
  timestamp: number,
  timezone: string,
  monthFormat: string,
) => {
  const range = momentRange.range(
    moment.tz(getScaleStart(timestamp, timezone, 'week') * 1000, timezone),
    moment.tz(getScaleEnd(timestamp, timezone, 'week') * 1000, timezone),
  );
  const days = Array.from(range.by('day', {excludeEnd: false}));
  return days.map((m) => {
    const dayRange = momentRange.range(
      moment.tz(getScaleStart(m.unix(), timezone, 'day') * 1000, timezone),
      moment.tz(getScaleEnd(m.unix(), timezone, 'day') * 1000, timezone),
    );
    const hours = Array.from(dayRange.by('hour', {excludeEnd: false}));
    const dayTimestamps = hours.map((h) => h.unix().toString());
    return [[m.format('ddd'), m.format(monthFormat)].join('\n'), dayTimestamps];
  }) as [string, string[]][];
};

const byWeek = (range: DateRange, options = {excludeEnd: false, step: 1}) => {
  return {
    [Symbol.iterator]() {
      const step = options.step || 1;
      const wStart = range.start.startOf('isoWeek');
      const wEnd = range.end.endOf('isoWeek');
      const diff = Math.abs(wStart.diff(wEnd, 'week')) / step;
      const excludeEnd = options.excludeEnd || false;
      let iteration = 0;

      return {
        next() {
          const current = range.start
            .startOf('isoWeek')
            .clone()
            .add(iteration * step, 'week');
          const done = excludeEnd ? !(iteration < diff) : !(iteration <= diff);

          iteration++;

          return {
            done,
            value: done ? undefined : current,
          };
        },
      };
    },
  };
};

export const groupByWeek = (timestamp: number, timezone: string) => {
  const range = momentRange.range(
    moment.tz(getScaleStart(timestamp, timezone, 'month') * 1000, timezone),
    moment.tz(getScaleEnd(timestamp, timezone, 'month') * 1000, timezone),
  );
  const weeks = Array.from(byWeek(range));
  return weeks.map((m) => {
    const wStart = moment.tz(
      getScaleStart(m.unix(), timezone, 'week') * 1000,
      timezone,
    );
    const wEnd = moment.tz(
      getScaleEnd(m.unix(), timezone, 'week') * 1000,
      timezone,
    );
    const dayRange = momentRange.range(wStart, wEnd);
    const days = Array.from(dayRange.by('day', {excludeEnd: false}));
    const dayTimestamps = days.map((h) => h.unix().toString());
    return [m.week(), dayTimestamps];
  }) as [number, string[]][];
};

export const groupByMonth = (timestamp: number, timezone: string) => {
  const range = momentRange.range(
    moment.tz(getScaleStart(timestamp, timezone, 'year') * 1000, timezone),
    moment.tz(getScaleEnd(timestamp, timezone, 'year') * 1000, timezone),
  );
  const months = Array.from(range.by('month', {excludeEnd: false}));
  return months.map((m) => {
    const monthRange = momentRange.range(
      moment.tz(getScaleStart(m.unix(), timezone, 'month') * 1000, timezone),
      moment.tz(getScaleEnd(m.unix(), timezone, 'month') * 1000, timezone),
    );
    const days = Array.from(monthRange.by('day', {excludeEnd: false}));
    const dayTimestamps = days.map((h) => h.unix().toString());
    return [monthStringToLocal((m.month() + 1).toString()), dayTimestamps];
  });
};

/**
 * U+2068 FIRST STRONG ISOLATE / U+2069 POP DIRECTIONAL ISOLATE.
 *
 * FSI rather than LRI (U+2066): the direction of a formatted duration is the
 * *content's* to decide, not the caller's. `11 hr 42 min` is LTR, `١١ س ٤٢ د`
 * is RTL, and FSI resolves each from its own first strong character. LRI would
 * force the Arabic form to run left-to-right, which is the bug in a different
 * costume.
 *
 * The isolate lives here, in the formatter, rather than in the 25 translations
 * that interpolate the result (FE #125): a per-string U+2066 is only correct
 * for the strings someone remembered to wrap, and `astral.upFor` was the one
 * that got noticed. Isolating at the source means no future string has to.
 */
const FIRST_STRONG_ISOLATE = '⁨';
const POP_DIRECTIONAL_ISOLATE = '⁩';

const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;

/**
 * An hours-and-minutes span, in the reader's language.
 *
 * Replaces three copies of `` `${hours}h${pad(minutes)}` `` (moon card, sun
 * card, sun detail) whose Latin `h` was emitted identically in all 25 locales
 * — correct in fr/pt/ca and wrong everywhere else (FE #130). German wants
 * `11 Std. 42 Min.`, Japanese `11時間42分`, Greek an `ω` rather than an `h`.
 *
 * The wording lives in ICU messages rather than `Intl.NumberFormat`'s unit
 * style, which is `Intl.NumberFormat` v3 (2020) and the part of the surface
 * Hermes is least likely to carry; the app force-polyfills `PluralRules` in
 * mobile's `src/i18n/setup.ts`, so its Intl support is demonstrably partial.
 *
 * French alone pads its minutes through the ICU skeleton
 * `::integer-width/*00`, so it reads `11h05` and not `11h5`. That does reach
 * `Intl.NumberFormat`, but only `minimumIntegerDigits` — an original-v1 option,
 * and one the app already depends on: `formatNumber` is used in 15 places
 * including the shipped moon card, so an engine without it has bigger problems
 * than this.
 *
 * Spans shorter than an hour drop the hours part and spans of a whole number of
 * hours drop the minutes, so neither `0 hr 42 min` nor `24 hr 0 min` — the
 * latter is what a midnight-sun day length would otherwise read.
 */
export const formatDuration = (
  seconds: number,
  intl: {
    formatMessage: (
      descriptor: MessageDescriptor,
      values?: Record<string, PrimitiveType>,
    ) => string;
  },
  /**
   * `'seconds'` keeps a seconds part below an hour: "3 min 25 s", "25 s". The
   * default drops it, because a duration people read as a length — a day
   * length, a pass — is noise at that resolution.
   *
   * Above an hour the seconds part is dropped either way. The sun card's daily
   * change reaches tens of minutes inside the Arctic Circle, where "+1 hr 4 min
   * 12 s" is precision nobody asked for; below an hour the seconds are the
   * whole point, because at most latitudes the change IS a couple of minutes.
   */
  {precision = 'minutes'}: {precision?: 'minutes' | 'seconds'} = {},
): string => {
  // `Math.max(0, NaN)` is `NaN`, so the clamp alone lets non-finite input
  // through to render as "NaN hr NaN min". Every caller null-guards before
  // subtracting two timestamps, but a total function is cheaper than trusting
  // that they always will.
  const total = Number.isFinite(seconds) ? Math.max(0, Math.round(seconds)) : 0;
  const hours = Math.floor(total / SECONDS_PER_HOUR);
  const minutes = Math.floor((total % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);

  let text: string;
  if (hours === 0 && precision === 'seconds') {
    const remainder = total % SECONDS_PER_MINUTE;
    text =
      minutes === 0
        ? intl.formatMessage(messages.calendar.durationSeconds, {
            seconds: remainder,
          })
        : intl.formatMessage(messages.calendar.durationMinutesSeconds, {
            minutes,
            seconds: remainder,
          });
  } else if (hours === 0) {
    text = intl.formatMessage(messages.calendar.durationMinutes, {minutes});
  } else if (minutes === 0) {
    text = intl.formatMessage(messages.calendar.durationHours, {hours});
  } else {
    text = intl.formatMessage(messages.calendar.durationHoursMinutes, {
      hours,
      minutes,
    });
  }

  return `${FIRST_STRONG_ISOLATE}${text}${POP_DIRECTIONAL_ISOLATE}`;
};
